import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Switch, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrashCan } from "@fortawesome/free-solid-svg-icons";

import styleCategories from "./Categories.module.css";
import { useDisclosure } from "../../hooks/useDisclosure";
import {
  useDeleteCategories,
  useGetCategoriesFilterList,
  useGetCategoriesFilterUpdate,
  useToggleCategoryGuestAccess,
} from "../../api/categories";
import { useGetLoggedInUser } from "../../api/loggedinUser";
import { isUberadmin } from "../../utils/permissions";
import StyledModal from "../../Components/Modal/StyledModal";

export const useColumns = refetch => {
  const navigate = useNavigate();
  const [deleteId, setDeleteId] = useState(null);
  const [activeById, setActiveById] = useState({});
  const [guestAccessById, setGuestAccessById] = useState({});
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { data: loggedInUserRes } = useGetLoggedInUser();
  const isAdmin = isUberadmin(loggedInUserRes?.data);
  const { mutateAsync: mutateAsyncDelete, isLoading: isLoadingDelete } =
    useDeleteCategories();
  const { mutateAsync: mutateGuestAccess } = useToggleCategoryGuestAccess();
  const { data: filterList } = useGetCategoriesFilterList();
  const { mutateAsync: mutateAsyncFilter } = useGetCategoriesFilterUpdate(
    (oldData, newData) => oldData
  );

  const filterActiveById = useMemo(() => {
    const map = {};
    (filterList || []).forEach(item => {
      map[item.id] = Boolean(item.value);
    });
    return map;
  }, [filterList]);

  useEffect(() => {
    setActiveById(filterActiveById);
  }, [filterActiveById]);

  const handleDelete = id => {
    onOpen();
    setDeleteId(id);
  };

  const handleDeleteCategories = () => {
    mutateAsyncDelete(deleteId)
      .then(res => {
        if (res?.data) {
          toast.success(res?.data?.message);
          onClose();
          refetch();
        }
      })
      .catch(err => {
        toast.error(err?.response?.data?.error);
        onClose();
      });
  };

  const handleGuestAccessToggle = (categoryId, nextValue) => {
    const previous = guestAccessById[categoryId];
    setGuestAccessById(prev => ({ ...prev, [categoryId]: nextValue }));
    mutateGuestAccess(categoryId)
      .then(res => {
        const confirmed = res?.data?.data?.guest_access;
        if (typeof confirmed === "boolean") {
          setGuestAccessById(prev => ({ ...prev, [categoryId]: confirmed }));
        }
      })
      .catch(err => {
        setGuestAccessById(prev => ({
          ...prev,
          [categoryId]: typeof previous === "boolean" ? previous : !nextValue,
        }));
        toast.error(err?.response?.data?.error || err?.message);
      });
  };

  const handleFilterToggle = categoryId => {
    setActiveById(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));

    mutateAsyncFilter({ data: { id: [categoryId] } }).catch(err => {
      setActiveById(prev => ({
        ...prev,
        [categoryId]: !prev[categoryId],
      }));
      toast.error(err?.response?.data?.error || err?.message);
    });
  };

  const columns = [
    {
      title: "S.N",
      render: ({ sn }) => {
        return <div>{sn}</div>;
      },
    },
    {
      title: "Name",
      render: ({ name }) => {
        return <div>{name}</div>;
      },
    },

    {
      title: "Is Active",
      render: row => {
        const isActive =
          activeById[row.id] ?? filterActiveById[row.id] ?? Boolean(row.show_in_filter);

        return (
          <Switch
            checked={isActive}
            onChange={() => handleFilterToggle(row.id)}
          />
        );
      },
    },
    ...(isAdmin
      ? [
          {
            title: "Guest Access",
            render: row => {
              const guestAccess =
                guestAccessById[row.id] ?? Boolean(row.guest_access);
              return (
                <Switch
                  checked={guestAccess}
                  onChange={checked => handleGuestAccessToggle(row.id, checked)}
                />
              );
            },
          },
        ]
      : []),
    {
      title: "Status",
      render: ({ status }) => {
        return <div>{status}</div>;
      },
    },
    {
      title: "Visibility",
      render: ({ visibility }) => {
        const value = visibility || "Public";
        return (
          <Tag color={value === "Private" ? "orange" : "green"}>{value}</Tag>
        );
      },
    },
    {
      title: "Action",
      render: row => {
        return (
          <div className="flex gap-2">
            <button
              onClick={() =>
                navigate(`/web-twinprocms/categories/${row.id}/update`, {
                  state: { item: row },
                })
              }
              className={styleCategories["modify-categories-btn"]}
            >
              <FontAwesomeIcon icon={faPen} />
            </button>{" "}
            <button
              onClick={() => handleDelete(row?.id)}
              className={styleCategories["delete-categories-btn"]}
            >
              <FontAwesomeIcon icon={faTrashCan} />
            </button>
            <StyledModal
              isOpen={isOpen}
              onClose={onClose}
              onConfirm={handleDeleteCategories}
              loading={isLoadingDelete}
            />
          </div>
        );
      },
    },
  ];
  return { columns };
};
